import { clamp } from "../../../foundation/utils/math";

type VerticalParams = {
    clientY: number;
    currentTop: number;
    topBound: number;
    bottomBound: number;
    maxScrollTop: number;
};

type HorizontalParams = {
    clientX: number;
    currentLeft: number;
    leftBound: number;
    rightBound: number;
    maxScrollLeft: number;
};

const SCROLL_OFFSET = 10; // px
const MIN_SCROLL_VELOCITY = 1; // px
const MAX_SCROLL_VELOCITY = 100; // px

/**
 * Checks if page should vertically scroll while drawing selectbox. If it shouldn't returns null.
 * @param clientY mouse event's origin on y-axis relative to the viewport
 * @param currentTop current top value of the scroll container on the current viewport considering scrolled amount
 * @param topBound top value of the scroll container
 * @param bottomBound bottom value of the scroll container
 * @param maxScrollTop maximum value that the scroll container can be scrolled
 * @returns number | null if should vertically scroll the new top value else null
 */
function getVerticalScroll({
    clientY, currentTop, topBound, bottomBound, maxScrollTop
}: VerticalParams) {
    const calculateDelta = (distance: number) => clamp(MIN_SCROLL_VELOCITY, distance, MAX_SCROLL_VELOCITY);
    const clampVerticalScroll = (value: number) => clamp(value, 0, maxScrollTop);

    // Scroll up
    const distanceToTop = topBound - clientY + SCROLL_OFFSET;
    if (distanceToTop > 0) {
        const delta = calculateDelta(distanceToTop);
        return clampVerticalScroll(currentTop - delta);
    }

    // Scroll down
    const distanceToBottom = clientY - bottomBound + SCROLL_OFFSET;
    if (distanceToBottom > 0) {
        const delta = calculateDelta(distanceToBottom);
        return clampVerticalScroll(currentTop + delta);
    }

    return null;
}

/**
 * Checks if page should horizontally scroll while drawing selectbox. If it shouldn't returns null.
 * @param clientX mouse event's origin on x-axis relative to the viewport
 * @param currentLeft current left value of the scroll container on the current viewport considering scrolled amount
 * @param leftBound left value of the scroll container
 * @param rightBound right value of the scroll container
 * @param maxScrollLeft maximum value that the scroll container can be scrolled
 * @returns number | null if should horizontally scroll the new left value else null
 */
function getHorizontalScroll({
    clientX, currentLeft, leftBound, rightBound, maxScrollLeft
}: HorizontalParams) {
    const calculateDelta = (distance: number) => clamp(MIN_SCROLL_VELOCITY, distance, MAX_SCROLL_VELOCITY);
    const clampHorizontalScroll = (value: number) => clamp(value, 0, maxScrollLeft);

    // Scroll left
    const distanceToLeft = leftBound - clientX + SCROLL_OFFSET;
    if (distanceToLeft > 0) {
        const delta = calculateDelta(distanceToLeft);
        return clampHorizontalScroll(currentLeft - delta);
    }

    // Scroll right
    const distanceToRight = clientX - rightBound + SCROLL_OFFSET;
    if (distanceToRight > 0) {
        const delta = calculateDelta(distanceToRight);
        return clampHorizontalScroll(currentLeft + delta);
    }

    return null;
}

export { getVerticalScroll, getHorizontalScroll };
